import {useCallback, useEffect, useRef, useState} from 'react';
import Icon from '../selfcare/Icon.tsx';
import useEvent from "react-use-event";
import {DispatchHighLightAddressSearchEvent, HighLightAddressSearchEvent} from "../../util/events.ts";
import CallMeFormComponent from "../callMe/CallMeFormComponent.tsx";
import LoaderSpinner from '../loaders/LoaderSpinner.tsx';
import {useDebounce} from "use-debounce";
import IconLookingGlass from "../icons/IconLookingGlass.tsx";

interface Result {
    data: {
        id: string
    }
    type: string
    forslagstekst: string
}

export default function OrderAddressSearchComponent(props: { autofocus?: boolean, notSticky?: boolean, largeAccountCustomerId?: string }) {
    const [query, setQuery] = useState('')
    const [queryBounced] = useDebounce(query, 50);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1)
    const [results, setResults] = useState<Result[]>([])
    const [redirecting, setRedirecting] = useState<boolean>(false)
    const [inFocus, setInFocus] = useState<boolean>(false)
    const [requestError, setRequestError] = useState<boolean>(false)
    const [hideErrorMessage, setHideErrorMessage] = useState<boolean>(false)
    const [sticky, setSticky] = useState<boolean>(false)

    const fetchResults = useCallback((q: string, startFrom?: string, accessAddressId?: string) => {
        if (q.length < 3) {
            setResults([])
            return;
        }

        const sf = startFrom !== undefined ? `&startfra=${startFrom}` : ''
        const aaid = accessAddressId !== undefined ? `&adgangsadresseid=${accessAddressId}` : ''

        fetch(`//api.dataforsyningen.dk/autocomplete?q=${q}&type=adresse&fuzzy=&per_side=20${sf}${aaid}`)
            .then((r) => {
                if (r.status !== 200) {
                    setRequestError(true)
                    return
                }

                r.json().then((j) => {
                    setResults(j)
                    setRequestError(false)
                })
            })

            .catch(() => {
                setRequestError(true)
            });
    }, []);

    const chooseResult = useCallback((r: Result) => {
        if (r.type === 'adresse') {
            setQuery(r.forslagstekst)
            setRedirecting(true)
            if (props.largeAccountCustomerId) {
                window.location.href = `/bestil/storkunde/${props.largeAccountCustomerId}/dawa/${r.data.id}`
            } else {
                window.location.href = '/bestil/dawa/' + r.data.id
            }
        } else {
            let txt = r.forslagstekst
            if (r.type == 'vejnavn') {
                txt = r.forslagstekst + ' '
            }

            setQuery(txt)
            setResults([])
            fetchResults(txt, 'adgangsadresse', r.data.id)
        }
    }, [props.largeAccountCustomerId, fetchResults])

    useEffect(() => {
        if (results.length > 0) {
            setSelectedIndex(0)
        }
    }, [results, chooseResult])

    useEffect(() => {
        fetchResults(queryBounced)
    }, [queryBounced, fetchResults])

    useEvent<HighLightAddressSearchEvent>('HighLightAddressSearchEvent', event => {
        setInFocus(event.show)
        input.current !== null && input.current.focus()
    })

    const input = useRef<HTMLInputElement>(null)
    const list = useRef<HTMLUListElement>(null)
    const aElem = useRef<HTMLLIElement>(null)
    const WrapElem = useRef<HTMLDivElement>(null)
    const HeaderHeight = useRef<HTMLDivElement>(null)

    const onScroll = useCallback(() => {
        if (!props.notSticky && WrapElem.current && HeaderHeight.current) {
            setSticky(WrapElem.current.getBoundingClientRect().top <= HeaderHeight.current.clientHeight)
        }
    }, [props.notSticky]);

    useEffect(() => {
        window.addEventListener('scroll', onScroll);
    }, [onScroll]);

    return (
        <>
            <div id="address-overlay" className={`${inFocus ? 'active' : ''}`} onClick={() => DispatchHighLightAddressSearchEvent(false)}/>
            <div ref={WrapElem} className='address-widget-wrap'>
                <div ref={HeaderHeight} className={'header-height'}/>
                <div className={`address-widget${sticky ? ' sticky' : ''}${query.length >= 3 ? ' typing' : ''}${inFocus ? ' focus' : ''}${requestError ? ' error' : ''}`}>
                    <div className='dawa-address'>
                        <div className='address-widget__left'>
                            <input
                                ref={input}
                                name='address_field'
                                id='address-field'
                                className={'search-input'}
                                autoFocus={props.autofocus}
                                type='text'
                                value={query}
                                disabled={redirecting}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder='Indtast din adresse'
                                autoComplete={'off'}
                                autoCapitalize={'off'}
                                spellCheck={'false'}
                                onKeyDown={(k) => {
                                    if (k.code === 'Enter') {
                                        chooseResult(results[selectedIndex])
                                    }

                                    if (k.code === 'ArrowDown') {
                                        k.preventDefault()
                                        setSelectedIndex((p) => {
                                            if (p + 1 > results.length - 1) {
                                                return p
                                            }

                                            if (list.current !== null && aElem.current !== null) {
                                                list.current.scrollTop += aElem.current.clientHeight
                                            }

                                            return p + 1
                                        })
                                    }

                                    if (k.code === 'ArrowUp') {
                                        k.preventDefault()
                                        setSelectedIndex((p) => {
                                            if (p - 1 < 0) {
                                                return p
                                            }

                                            if (list.current !== null && aElem.current !== null) {
                                                list.current.scrollTop -= aElem.current.clientHeight
                                            }

                                            return p - 1
                                        })
                                    }
                                }}
                            />

                            {!requestError && results !== undefined && results.length > 0 && (
                                <div className='dawa-address__propositions-wrap visible'>
                                    <ul className='dawa-address__address-list' id={"address-list"} ref={list}>
                                        {results.map((r, i) => {
                                            return (
                                                <li
                                                    key={i}
                                                    ref={(i === 0 ? aElem : null)}
                                                    className={`${selectedIndex === i ? 'focus' : ''}`}
                                                    onClick={() => {
                                                        chooseResult(r)
                                                        input.current?.focus()
                                                    }}
                                                >
                                                    <span>{r.forslagstekst}</span>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            )}

                            {requestError && (
                                <div className={'rmo-container'}>
                                    {!hideErrorMessage && (<>
                                        <h4 className={'heading heading--montserrat heading--medium'}>
                                            Der er i øjeblikket problemer med adresseopslag
                                        </h4>
                                        <p>Indtast dit telefonnummer herunder, så ringer vi dig op.</p>
                                    </>)}
                                    <CallMeFormComponent
                                        onSubmitForm={() => {
                                            setHideErrorMessage(true)
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div
                            className={`address-widget__right`}
                            data-single-result={results.length === 1 && results[0].type === 'adresse' && selectedIndex === 0}
                            id={"continue-button"}
                            onClick={() => chooseResult(results[selectedIndex])}
                        >
                            {redirecting ? <LoaderSpinner/> : <Icon component={IconLookingGlass}/>}
                        </div>
                    </div>
                    {query.length === 0 && !requestError && (
                        <figure className={'address-widget-shimmer'}/>
                    )}
                </div>
            </div>
        </>
    )
}
